import { Copy, Link2, Unlink } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  useLinkDivisionHandleMutation,
  useListDivisionHandlesQuery,
  useUnlinkDivisionHandleMutation,
} from '@/services/api/carriersApiService';

import { HANDLE_TYPE_ICONS } from './constants';
import type { ContactHandle } from './types';

interface DivisionHandlesInlineProps {
  carrierId: string;
  divisionId: string;
  divisionName: string;
  carrierHandles: ContactHandle[];
}

export const DivisionHandlesInline = ({
  carrierId,
  divisionId,
  divisionName,
  carrierHandles,
}: DivisionHandlesInlineProps) => {
  const { data: linkedHandles = [] } = useListDivisionHandlesQuery(
    { carrierId, divisionId },
    { skip: !carrierId || !divisionId },
  );
  const [linkHandle] = useLinkDivisionHandleMutation();
  const [unlinkHandle] = useUnlinkDivisionHandleMutation();

  const linkedSet = useMemo(
    () => new Set(linkedHandles.map((h) => h.id)),
    [linkedHandles],
  );

  const [selectedHandleId, setSelectedHandleId] = useState<string>('');
  const unlinkedHandles = useMemo(
    () => carrierHandles.filter((h) => !linkedSet.has(h.id) && !h.archivedAt),
    [carrierHandles, linkedSet],
  );

  const handleAdd = async () => {
    if (!selectedHandleId) return;
    try {
      await linkHandle({
        carrierId,
        divisionId,
        handleId: selectedHandleId,
      }).unwrap();
      setSelectedHandleId('');
      toast.success('Handle linked to division');
    } catch {
      toast.error('Failed to link handle');
    }
  };

  const handleRemove = async (handleId: string) => {
    try {
      await unlinkHandle({ carrierId, divisionId, handleId }).unwrap();
      toast.success('Handle unlinked');
    } catch {
      toast.error('Failed to unlink handle');
    }
  };

  const handleCopy = async (handle: ContactHandle) => {
    const value =
      handle.handleType === 'MAILING_ADDRESS'
        ? `${handle.addressLine1 || ''} ${handle.city || ''}, ${handle.state || ''} ${handle.zip || ''}`.trim()
        : handle.value || '';

    await navigator.clipboard.writeText(value);
    toast.success('Copied to clipboard');
  };

  return (
    <div className="space-y-3">
      {/* Linked handles list */}
      {linkedHandles.length === 0 ? (
        <div className="text-center py-4">
          <p className="text-sm text-gray-400">
            No contacts linked to {divisionName}
          </p>
        </div>
      ) : (
        <div className="space-y-1.5">
          {linkedHandles.map((h) => {
            const Icon = HANDLE_TYPE_ICONS[h.handleType];
            const displayValue =
              h.handleType === 'MAILING_ADDRESS'
                ? `${h.addressLine1 || ''} ${h.city || ''}`
                : h.value || '';

            return (
              <div
                key={h.id}
                className="group flex items-center gap-2 px-3 py-2 bg-white border border-gray-100 rounded-lg hover:border-gray-200 transition-all"
              >
                <div className="flex-shrink-0 w-7 h-7 bg-gray-50 rounded flex items-center justify-center">
                  <Icon className="w-3.5 h-3.5 text-gray-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">
                    {h.name || h.handleType}
                  </div>
                  <div className="text-xs text-gray-500 truncate">
                    {displayValue}
                  </div>
                </div>
                <div className="flex-shrink-0 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    type="button"
                    onClick={() => handleCopy(h)}
                    className="p-1 text-gray-400 hover:text-primary hover:bg-primary/5 rounded transition-colors"
                    title="Copy"
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    className="p-1 text-gray-400 hover:text-error hover:bg-error/5 rounded transition-colors"
                    title="Remove from division"
                    onClick={() => handleRemove(h.id)}
                  >
                    <Unlink className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add handle control */}
      {unlinkedHandles.length > 0 && (
        <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
          <Link2 className="w-4 h-4 text-gray-300 flex-shrink-0" />
          <select
            value={selectedHandleId}
            onChange={(e) => setSelectedHandleId(e.target.value)}
            className="flex-1 px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none bg-white"
          >
            <option value="">Link existing contact...</option>
            {unlinkedHandles.map((h) => {
              const displayValue =
                h.handleType === 'MAILING_ADDRESS'
                  ? h.addressLine1 || ''
                  : h.value || '';
              return (
                <option key={h.id} value={h.id}>
                  {h.name || h.handleType} — {displayValue}
                </option>
              );
            })}
          </select>
          <Button
            size="sm"
            variant="outline"
            onClick={handleAdd}
            disabled={!selectedHandleId}
          >
            Link
          </Button>
        </div>
      )}
    </div>
  );
};
